import Swiper from 'swiper/bundle';

window.sectionSwiperRegistry = window.sectionSwiperRegistry || new Map();
class SwiperInitializer {
  constructor(root = document) {
    this.sections = root.querySelectorAll('.swiper-section');
    this.timeoutID = null;
    this.swiperInstances = new Map(); // Store swiper instances for syncing
    this.handleResize = this.handleResize.bind(this);
    this.defaultOptions = {
      slidesPerView: 1,
      spaceBetween: 20,
      a11y: true,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        type: 'bullets'
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },
      speed: 300,
      autoplay: false,
      on: {
        init: function () {
          // Set tabindex to -1 for all focusable elements in inactive slides
          const focusableElements = 'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';
          this.slides.forEach((slide, i) => {
            const isVisible = slide.classList.contains('swiper-slide-visible');
            if (!isVisible) {
              slide.querySelectorAll(focusableElements).forEach((el) => {
                el.setAttribute('tabindex', '-1');
              });
            }
            setTimeout(() => {
              this.slides.forEach((slide, i) => {
                const isProduct = !!slide.querySelector('.product-card-wrapper');
                const label = `${isProduct ? 'Product' : 'Slide'} ${i + 1} of ${this.slides.length}`;
                slide.setAttribute('aria-label', label);
              });
            }, 50);
          });
        },
        slideChange: function () {
          // Manage tabindex on slide change
          const focusableElements = 'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';
          this.slides.forEach((slide, i) => {
            const isVisible = slide.classList.contains('swiper-slide-visible');
            slide.querySelectorAll(focusableElements).forEach((el) => {
              if (isVisible) {
                el.removeAttribute('tabindex');
              } else {
                el.setAttribute('tabindex', '-1');
              }
            });

            const isProduct = !!slide.querySelector('.product-card-wrapper');
            const label = `${isProduct ? 'Product' : 'Slide'} ${i + 1} of ${this.slides.length}`;
            slide.setAttribute('aria-label', label);
          });

          // --- Begin mobile-flip logic ---
          const sectionEl = this.el.closest('.swiper-section');
          const mobileFlipAttr = sectionEl ? sectionEl.getAttribute('data-mobile-flip') : null;
          if (mobileFlipAttr) {
            const mobileFlipDelay = parseInt(mobileFlipAttr, 10);
            this.slides.forEach(slide => {
              slide.classList.remove('custom-swiper-slide-active');
            });
            if (this.timeoutID) {
              clearTimeout(this.timeoutID);
            }
            this.timeoutID = setTimeout(() => {     
              const activeSlide = this.slides[this.activeIndex];
              activeSlide.classList.add('custom-swiper-slide-active');
            }, isNaN(mobileFlipDelay) ? 1000 : mobileFlipDelay);
          }
          // --- End mobile-flip logic ---
        },
        observerUpdate: function () {
          this.slides.forEach((slide, i) => {
            const isProduct = !!slide.querySelector('.product-card-wrapper');
            const label = `${isProduct ? 'Product' : 'Slide'} ${i + 1} of ${this.slides.length}`;
            slide.setAttribute('aria-label', label);
          });
        }
      }
    };
  }

  isMobile(section) {
    // Get breakpoint from data attribute, fallback to 900
    const breakpoint = parseInt(section.dataset.mobileBreakpoint);
    if (!breakpoint) {
      return true;
    }
    return window.innerWidth <= breakpoint;
  }

  setupNestedSwiperEvents(swiperElement) {
    // Get the swiper instance safely
    const swiperInstance = swiperElement.swiper;
    if (!swiperInstance) {
      return;
    }

    // Simple click-only handling for decorative nested swipers
    const handleNestedClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      
      // Ensure swiper is still valid and enabled
      if (swiperInstance && swiperInstance.enabled && typeof swiperInstance.slideNext === 'function') {
        let speed = swiperInstance.params.speed ? parseInt(swiperInstance.params.speed) : 2000;
        try {
          swiperInstance.slideNext(speed);
        } catch (error) {
          console.warn('Error sliding nested swiper:', error);
        }
      }
    };

    // Add only click event listener (no keyboard navigation for decorative carousels)
    swiperElement.addEventListener('click', handleNestedClick, { passive: false });
    
    // Store handler for cleanup
    swiperInstance.nestedClickHandler = handleNestedClick;
  }

  initOrDestroySwipers() {
    this.sections.forEach((section, index) => {
      // Find all swiper elements within this section
      const swiperElements = section.querySelectorAll('.swiper');
      const swipers = [];

      if (!swiperElements.length) {
        return;
      }

      // Check if this section should be mobile-only first
      const isMobile = this.isMobile(section);
      
      if (isMobile) {
        // Only initialize if not already initialized
        if (!this.swiperInstances.has(section)){
          // Separate parent and nested swipers to ensure correct initialization order
          const parentSwipers = Array.from(swiperElements).filter(el => !el.classList.contains('nested-swiper'));
          const nestedSwipers = Array.from(swiperElements).filter(el => el.classList.contains('nested-swiper'));
          
          // Initialize parent swipers first
          [...parentSwipers, ...nestedSwipers].forEach((swiperElement, swiperIndex) => {
            // For nested swiper sections, use their own data attributes
            const parentSwiperSection = swiperElement.closest('.swiper-section');
            const options = this.getOptionsFromAttributes(
              parentSwiperSection !== section ? parentSwiperSection : section
            );
            
            // For toggled swiper sections, we need to initialize the swiper for each tab
            if (parentSwiperSection.dataset.toggledSwiper === 'true') {
              const toggledSwipers = {};
              section.querySelectorAll('.carousel-tab').forEach(btn => {
                btn.addEventListener('click', e => {
                  this.handleTabActivation(e.currentTarget, toggledSwipers, swipers, section, parentSwiperSection);
                });
                btn.addEventListener('keydown', e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.handleTabActivation(btn, toggledSwipers, swipers, section, parentSwiperSection);
                  }
                });
              });
              const firstTab = section.querySelector('.carousel-tab');
              if (firstTab) firstTab.click();
            } else {
              // For nested swipers, add a delay to ensure parent is fully initialized
              const initSwiper = () => {

                if (swiperElement.classList.contains('nested-swiper')) {
                  // Find the parent card container
                  const cardContainer = swiperElement.closest('[data-nested-carousel-parent="true"]');
                  if (cardContainer) {
                    // Look for the pagination element within the card's content area
                    const paginationElement = cardContainer.querySelector('[data-nested-carousel-pagination="true"]');
                    if (paginationElement) {
                      // Override the pagination option to use the specific element
                      options.pagination = {
                        el: paginationElement,
                        clickable: true,
                        type: 'bullets'
                      };
                    }
                  }
                }
                const swiper = new Swiper(swiperElement, options);
                swipers.push(swiper);
                window.sectionSwiperRegistry.set(section.id, swiper);

                // Store reference to SwiperInitializer and its swiperInstances
                swiper.swiperInitializer = this;
                swiper.swiperInstances = this.swiperInstances;
                
                // Add cleanup handler for proper memory management
                swiper.on('destroy', () => {
                  if (swiper.mutationObserver) {
                    swiper.mutationObserver.disconnect();
                    swiper.mutationObserver = null;
                  }
                  if (swiper.autoplayInterval) {
                    clearInterval(swiper.autoplayInterval);
                    swiper.autoplayInterval = null;
                  }
                  // Clean up nested swiper event handlers
                  if (swiper.nestedClickHandler && swiperElement) {
                    swiperElement.removeEventListener('click', swiper.nestedClickHandler);
                  }
                  // Clean up any stored references
                  if (swiper.swiperInitializer) {
                    swiper.swiperInitializer = null;
                  }
                  if (swiper.swiperInstances) {
                    swiper.swiperInstances = null;
                  }
                });

                // Handle Nested Swipers - simplified for decorative carousels
                if (swiperElement.classList.contains('nested-swiper')) {
                  // For decorative carousels: no focus management needed
                  // Remove from tab order (handled by aria-hidden in HTML)
                  swiperElement.removeAttribute('tabindex');
                  
                  // Ensure pagination dots are not focusable
                  swiper.on('init', () => {
                    const paginationBullets = swiperElement.querySelectorAll('.swiper-pagination-bullet');
                    paginationBullets.forEach(bullet => {
                      bullet.setAttribute('tabindex', '-1');
                      bullet.setAttribute('aria-hidden', 'true');
                    });
                  });
                } else {
                  // Ensure parent swipers are NOT focusable
                  swiperElement.setAttribute('tabindex', '-1');
                }

                // Store swiper instance if it's part of a sync group
                if (parentSwiperSection.dataset.synced === 'true' && parentSwiperSection.dataset.syncId) {
                  if (!this.swiperInstances.has(parentSwiperSection.dataset.syncId)) {
                    this.swiperInstances.set(parentSwiperSection.dataset.syncId, []);
                  }
                  this.swiperInstances.get(parentSwiperSection.dataset.syncId).push(swiper);
                }
              };
              
              // Initialize nested swipers with delay, others immediately
              if (swiperElement.classList.contains('nested-swiper')) {
                setTimeout(() => {
                  initSwiper();
                  // Add event handlers after swiper is fully initialized
                  this.setupNestedSwiperEvents(swiperElement);
                }, 150);
              } else {
                initSwiper();
              }
            }
          });
          this.swiperInstances.set(section, swipers);
        }
      } else {
        // Destroy if initialized (desktop mode)
        if (this.swiperInstances.has(section)) {
          this.swiperInstances.get(section).forEach(swiper => {
            swiper.destroy(true, true);
          });
          this.swiperInstances.delete(section);
        }
      }

      // Setup controls if they exist
      const controls = section.querySelector('.swiper-controls');
      if (controls) {
        const playButton = controls.querySelector('#play-button');
        const pauseButton = controls.querySelector('#pause-button');

        if (playButton && pauseButton) {
          // Function to update button states
          const updateButtonStates = (isPlaying) => {
            playButton.setAttribute('aria-pressed', !isPlaying);
            pauseButton.setAttribute('aria-pressed', isPlaying);
            playButton.style.display = isPlaying ? 'none' : 'block';
            pauseButton.style.display = isPlaying ? 'block' : 'none';
          };

          // Initial state
          const isAutoplayRunning = swipers.some(swiper => swiper.autoplay?.running);
          updateButtonStates(isAutoplayRunning);

          // Play button click handler
          playButton.addEventListener('click', () => {
            swipers.forEach(swiper => {
              if (swiper.autoplay) {
                swiper.autoplay.start();
              }
            });
            updateButtonStates(true);
          });

          // Pause button click handler
          pauseButton.addEventListener('click', () => {
            swipers.forEach(swiper => {
              if (swiper.autoplay) {
                swiper.autoplay.stop();
              }
            });
            updateButtonStates(false);
          });

          // Update button states when autoplay state changes
          swipers.forEach(swiper => {
            if (swiper.autoplay) {
              swiper.on('autoplayStart', () => {
                updateButtonStates(true);
              });
              swiper.on('autoplayStop', () => {
                updateButtonStates(false);
              });
            }
          });
        }
      }
    });

    // Initialize sync groups
    this.initSyncGroups();
  }
 
  initSyncGroups() {
    this.swiperInstances.forEach((swipers, syncId) => {
      if (swipers.length < 2) return;

      // Find the parent swiper and synced swipers from the array
      const parentSwiper = swipers.find(swiper => 
        swiper.el.closest('.swiper-section').dataset.parentSwiper === 'true'
      );
      
      if (!parentSwiper) {
        return;
      }

      const syncedSwipers = swipers.filter(swiper => 
        swiper !== parentSwiper && 
        swiper.el.closest('.swiper-section').dataset.parentSwiper === 'false'
      );

      // Add slide change handler to parent swiper
      parentSwiper.on('slideChange', () => {
        const activeIndex = parentSwiper.realIndex;
        syncedSwipers.forEach(swiper => {
          swiper.slideToLoop(activeIndex, 300);
        });
      });

      // Initial sync
      const activeIndex = parentSwiper.realIndex;
      syncedSwipers.forEach(swiper => {
        swiper.slideToLoop(activeIndex, 300);
      });
    });
  }

  handleTabActivation(tabBtn, toggledSwipers, swipers, section, parentSwiperSection) {
    const id = tabBtn.dataset.target;
    const wrapper = section.querySelector('#' + id).parentElement;
    let tabCollection = tabBtn.dataset.tabCollection;
    let tabCollectionUrl = tabBtn.dataset.tabCollectionUrl;
    if (tabCollection && tabCollectionUrl) {
      section.querySelectorAll('.featured-collections__view-all').forEach(viewAll => {
        viewAll.setAttribute('href', tabCollectionUrl);
      });
    }

    section.querySelectorAll('.carousel-tabs').forEach(tablist => {
      const tabs = Array.from(tablist.querySelectorAll('.carousel-tab'));
      tabs.forEach((tab, idx) => {
        tab.addEventListener('keydown', e => {
          let newIdx = null;
          if (e.key === 'ArrowRight') {
            newIdx = (idx + 1) % tabs.length;
          } else if (e.key === 'ArrowLeft') {
            newIdx = (idx - 1 + tabs.length) % tabs.length;
          }
          if (newIdx !== null) {
            e.preventDefault();
            tabs[newIdx].focus();
            tabs[newIdx].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
          }
        });
        tab.addEventListener('focus', () => {
          tab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        });
      });
    });

    // Add active class to tab button
    section.querySelectorAll('.carousel-tab').forEach((btn, i) => {
      btn.classList.toggle('is-active', btn === tabBtn);
      btn.setAttribute('aria-selected', btn === tabBtn ? 'true' : 'false');
    });
    section.querySelectorAll('.carousel-wrapper').forEach((panel, i) => {
      if (panel === wrapper) {
        panel.classList.remove('is-hidden');
        panel.removeAttribute('hidden');
        panel.setAttribute('aria-hidden', 'false');
      } else {
        panel.classList.add('is-hidden');
        panel.setAttribute('hidden', '');
        panel.setAttribute('aria-hidden', 'true');
      }
    });
  
    // Lazy-init or update Swiper
    if (!toggledSwipers[id]) {
      const options = this.getOptionsFromAttributes(section);
      options.init = false; // Don't auto-init
      toggledSwipers[id] = new Swiper('#' + id, options);
      const swiper = toggledSwipers[id];
      swipers.push(swiper);
      window.sectionSwiperRegistry.set(section.id, swiper);

      swiper.swiperInitializer = this;
      swiper.swiperInstances = this.swiperInstances;
  
      // Sync group logic for toggled swipers (if needed)
      if (parentSwiperSection.dataset.synced === 'true' && parentSwiperSection.dataset.syncId) {
        if (!this.swiperInstances.has(parentSwiperSection.dataset.syncId)) {
          this.swiperInstances.set(parentSwiperSection.dataset.syncId, []);
        }
        this.swiperInstances.get(parentSwiperSection.dataset.syncId).push(swiper);
      }
  
      toggledSwipers[id].init();
    } else {
      toggledSwipers[id].update();
    }
  }

  getOptionsFromAttributes(section) {
    const options = { ...this.defaultOptions };
    const data = section.dataset;

    // Handle basic numeric values
    if (data.slidesPerView) {
      const slidesPerViewValue = parseFloat(data.slidesPerView);
      options.slidesPerView = isNaN(slidesPerViewValue) ? 'auto' : slidesPerViewValue;
    }
    if (data.spaceBetween) {
      options.spaceBetween = parseInt(data.spaceBetween);
    }
    if (data.speed) {
      options.speed = parseInt(data.speed);
    }
    if (data.centeredSlides) {
      options.centeredSlides = data.centeredSlides === 'true';
    }
    if (data.loop) {
      options.loop = data.loop === 'true';
    }
    if (data.loopedSlides) {
      options.loopedSlides = parseInt(data.loopedSlides);
    }
    if (data.watchSlidesProgress) {
      options.watchSlidesProgress = data.watchSlidesProgress === 'true';
    }
    if (data.centerInsufficientSlides) {
      options.centerInsufficientSlides = data.centerInsufficientSlides === 'true';
    }
    if (data.slidesPerGroup) {
      options.slidesPerGroup = parseInt(data.slidesPerGroup);
    }
    if (data.slidesOffsetBefore) {
      options.slidesOffsetBefore = parseInt(data.slidesOffsetBefore);
    }
    if (data.slidesOffsetAfter) {
      options.slidesOffsetAfter = parseInt(data.slidesOffsetAfter);
    }
    if (data.freeMode === 'true') {
      options.freeMode = true;
    } else if (data.freeMode === 'false') {
      options.freeMode = false;
    }

    if (data.longSwipes) {
      options.longSwipes = data.longSwipes === 'true';
    }
    
    if (data.centeredSlides) {
      options.centeredSlides = data.centeredSlides === 'true';
    }

    // Handle pagination
    if (data.pagination === 'false') {
      options.pagination = false;
    } else {
      let paginationContainer = null;
      
      // For nested carousels, check if there's a custom pagination ID
      if (data.nestedCarousel === 'true' && data.paginationId) {
        // Look for pagination by ID in the broader document context
        paginationContainer = document.getElementById(data.paginationId);
      } else {
        // Look for custom pagination container within section
        paginationContainer = section.querySelector('[class*="__pagination"]');
      }
      
      if (paginationContainer) {
        options.pagination = {
          el: paginationContainer,
          clickable: true,
          type: data.paginationType || 'bullets'
        };
      } else {
        // Fallback to default pagination
        options.pagination = {
          el: '.swiper-pagination',
          clickable: true,
          type: data.paginationType || 'bullets'
        };
      }
    }

    // Handle navigation
    if (data.navigation === 'false') {
      options.navigation = false;
    } else {
      // Look for custom navigation buttons in the section
      const prevButton = section.querySelector('[class*="__button--prev"]');
      const nextButton = section.querySelector('[class*="__button--next"]');
      
      if (prevButton && nextButton) {
        options.navigation = {
          prevEl: prevButton,
          nextEl: nextButton,
        };
      } else {
        // Fallback to default navigation if custom buttons aren't found
        options.navigation = {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next',
        };
      }
    }

    if (data.rewind === 'true') {
      options.rewind = true;
    }

    // Handle scrollbar
    if (data.scrollbar === 'true') {
      options.scrollbar = {
        el: '.swiper-scrollbar',
        draggable: true
      };
    }

    // Handle autoplay
    if (data.autoplay === 'true') {
      // Skip complex autoplay setup for nested swipers to prevent conflicts
      const isNested = section.dataset.nestedCarousel === 'true';
      if (isNested) {
        // Simple autoplay for nested swipers
        const delay = data.speed ? parseInt(data.speed) : 5000;
        const disableOnInteraction = data.autoplayDisableOnInteraction !== 'false';
        const pauseOnMouseEnter = data.autoplayPauseOnMouseEnter === 'true';
        options.autoplay = {
          delay: delay,
          disableOnInteraction: disableOnInteraction,
          pauseOnMouseEnter: pauseOnMouseEnter
        };
        return options;
      }
      
      // Check if section has card-carousel__inner
      const hasCardCarouselInner = section.querySelector(':scope > .card-carousel__inner') !== null;
      // Build selector based on structure
      const slideSelector = hasCardCarouselInner 
        ? ':scope > .card-carousel__inner > .swiper > .swiper-wrapper > .swiper-slide'
        : ':scope > .swiper > .swiper-wrapper > .swiper-slide';
      const Slides = section.querySelectorAll(slideSelector)
      const totalSlides = Slides.length;
      const delay = data.autoplayDelay ? parseInt(data.autoplayDelay) : 3000;
      
      options.autoplay = {
        delay: delay,
        disableOnInteraction: data.autoplayDisableOnInteraction !== 'false',
        pauseOnMouseEnter: data.autoplayPauseOnMouseEnter === 'true'
      };

      // Add custom autoplay behavior only if data-custom-autoplay is true
      if (data.customAutoplay === 'true') {
        options.on = {
          ...options.on,
          init: function() {
            // Original init behavior
            this.slides.forEach((slide, index) => {
              if (index >= this.params.slidesPerView) {
                slide.setAttribute('aria-hidden', 'true');
              }
            });

            // Function to check if we're on desktop
            const isDesktop = () => {
              return window.innerWidth >= 990; // Match your breakpoint
            };

            // Function to setup custom autoplay
            const setupCustomAutoplay = () => {
              if (this.autoplayInterval) {
                clearInterval(this.autoplayInterval);
              }

              if (isDesktop() && totalSlides <= this.params.slidesPerView) {
                const swiper = this;
                let currentIndex = swiper.activeIndex;

                // Function to update active classes
                const updateActiveClasses = (newIndex) => {
                  // Get the sync group
                  const syncId = swiper.el.closest('.swiper-section').dataset.syncId;
                  const syncGroup = swiper.swiperInstances.get(syncId);

                  if (syncGroup) {
                    // Update all swipers in the sync group
                    syncGroup.forEach(syncedSwiper => {
                      // Skip the parent swiper as it's already updated
                      if (syncedSwiper === swiper) return;

                      // Use Swiper's native slideTo method
                      syncedSwiper.slideTo(newIndex, swiper.params.speed, true);
                    });
                  }

                  // Update the parent swiper
                  swiper.slides.forEach(slide => {
                    slide.classList.remove('swiper-slide-active', 'swiper-slide-prev', 'swiper-slide-next');
                  });

                  // Add active class to current slide
                  swiper.slides[newIndex].classList.add('swiper-slide-active');

                  // Add prev class
                  const prevIndex = newIndex === 0 ? swiper.slides.length - 1 : newIndex - 1;
                  swiper.slides[prevIndex].classList.add('swiper-slide-prev');

                  // Add next class
                  const nextIndex = newIndex === swiper.slides.length - 1 ? 0 : newIndex + 1;
                  swiper.slides[nextIndex].classList.add('swiper-slide-next');

                  // Update current index
                  currentIndex = newIndex;
                };

                this.autoplayInterval = setInterval(() => {
                  if (!swiper.el.matches(':hover') || !swiper.params.autoplay.pauseOnMouseEnter) {
                    const nextIndex = currentIndex === swiper.slides.length - 1 ? 0 : currentIndex + 1;
                    updateActiveClasses(nextIndex);
                  }
                }, delay);

                // Add hover handlers for each slide
                this.slides.forEach((slide, index) => {
                  slide.addEventListener('mouseenter', () => {
                    if (swiper.autoplayInterval) {
                      clearInterval(swiper.autoplayInterval);
                      swiper.autoplayInterval = null;
                    }
                    updateActiveClasses(index);
                  });

                  slide.addEventListener('mouseleave', () => {
                    // Clear any existing interval first
                    if (swiper.autoplayInterval) {
                      clearInterval(swiper.autoplayInterval);
                    }
                    // Start a new interval
                    swiper.autoplayInterval = setInterval(() => {
                      const nextIndex = currentIndex === swiper.slides.length - 1 ? 0 : currentIndex + 1;
                      updateActiveClasses(nextIndex);
                    }, delay);
                  });
                });
              }
            };

            // Initial setup
            setupCustomAutoplay();
          },
          destroy: function() {
            // Clean up interval when swiper is destroyed
            if (this.autoplayInterval) {
              clearInterval(this.autoplayInterval);
            };
          }
        };
      }
    }

    // Handle breakpoints
    if (data.breakpoints) {
      try {
        const breakpoints = JSON.parse(data.breakpoints);
        
        // Process each breakpoint to handle grid rows
        Object.keys(breakpoints).forEach(breakpoint => {
          const breakpointOptions = breakpoints[breakpoint];
          
          // Handle slidesPerView - keep 'auto' as string, convert numbers
          if (breakpointOptions.slidesPerView !== 'auto') {
            const slidesPerView = parseFloat(breakpointOptions.slidesPerView);
            breakpointOptions.slidesPerView = isNaN(slidesPerView) ? breakpointOptions.slidesPerView : slidesPerView;
          }

          // Add fixLoop and its handlers for mobile breakpoints (0 and 375) only if fixLoop is enabled
          if ((breakpoint === '0' || breakpoint === '375') && data.fixLoop === 'true') {
            breakpointOptions.on = {
              init: function() {
                if (this.params.loop) {
                  this.fixLoop();
                }
              },
              slideChange: function() {
                if (this.params.loop) {
                  this.fixLoop();
                }
              }
            };
          }

        });

        options.breakpoints = breakpoints;
      } catch (e) {
        console.error('Error parsing breakpoints:', e);
      }
    }

    // Handle vertical direction for synced sliders
    if (data.synced === 'true' && data.direction === 'vertical') {
      options.direction = 'vertical';
      options.allowTouchMove = false;
      options.watchSlidesProgress = true;
    }

    // Handle observer for dynamic resizing
    if (data.observer === 'true') {
      options.observer = true;
      options.observeParents = true;
      options.observeSlideChildren = data.observeSlideChildren === 'true';
    }

    // Handle resize observer for responsive behavior
    if (data.resizeObserver === 'true') {
      options.resizeObserver = true;
    }

    return options;
  }

  init() {
    this.initOrDestroySwipers();
    window.addEventListener('resize', this.handleResize);
  }

  handleResize() {
    this.initOrDestroySwipers();
  }
}

function destroySwipersInSection(section) {
  const swipers = window.sectionSwiperRegistry.get(section);
  if (swipers && swipers.length) {
    swipers.forEach(swiper => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    window.sectionSwiperRegistry.delete(section);
  }
}
window.destroySwipersInSection = destroySwipersInSection;

function reinitSwipers(target = document) {
  // Destroy old swipers in the target
  if (target instanceof Element) {
    destroySwipersInSection(target);
  } else {
    // If re-initializing the whole page, destroy all
    window.sectionSwiperRegistry.forEach((_, section) => destroySwipersInSection(section));
  }
  // Re-initialize
  new SwiperInitializer(target).init();
}
window.reinitSwipers = reinitSwipers;

document.addEventListener('DOMContentLoaded', () => {
  new SwiperInitializer().init();
});

